import { get, writable } from 'svelte/store';

import { isValidActionValue, validateWorkflowForImport } from '@client/pages/staff/workflows/workflow-validation';

import { SettingsService } from '@client/services/settings';

import { userStore } from '@client/stores/user';

import { generateUUID } from '@common/utils/strings';

/**
 * Sanitize a workflow's steps by filtering out any steps with invalid action values.
 * This protects against stored XSS from workflows imported before the validation fix.
 */
const sanitizeWorkflowSteps = (workflow) => {
  if (!workflow?.steps || !Array.isArray(workflow.steps)) {
    return workflow;
  }

  const sanitizedSteps = workflow.steps.filter((step) => {
    const actionValue = typeof step.action === 'string' ? step.action : step.action?.value;
    return isValidActionValue(actionValue);
  });

  // If steps were removed, return the sanitized workflow
  if (sanitizedSteps.length !== workflow.steps.length) {
    console.warn(
      `Workflow "${workflow.name}" had ${workflow.steps.length - sanitizedSteps.length} invalid step(s) removed during sanitization.`
    );
    return { ...workflow, steps: sanitizedSteps };
  }

  return workflow;
};

/**
 * Sanitize all workflows loaded from settings.
 */
const sanitizeWorkflows = (workflows) => {
  if (!Array.isArray(workflows)) return [];
  return workflows.map(sanitizeWorkflowSteps);
};

// Initialize workflows from userStore.usersettings
const initializeWorkflows = () => {
  const user = get(userStore);
  const workflows = user?.settings?.workflows || [];
  return sanitizeWorkflows(workflows);
};

// Create the workflows store
export const workflowsStore = writable(initializeWorkflows());

// Create the loading store - true until workflows are initialized
export const workflowsLoading = writable(true);

// Re-initialize workflows when userStore changes (login, domain switch, etc.)
userStore.subscribe((user) => {
  if (!user) return;

  const workflows = user?.settings?.workflows;
  if (Array.isArray(workflows)) {
    workflowsStore.set(sanitizeWorkflows(workflows));
  }
  workflowsLoading.set(false);
});

// Create a new workflow
export const createWorkflow = async (workflowData) => {
  const workflows = get(workflowsStore);

  // Simple workflow structure - just name and steps
  const newWorkflow = {
    name: workflowData.name,
    steps: workflowData.steps
  };

  const updatedWorkflows = [...workflows, newWorkflow];

  // Optimistic update
  workflowsStore.set(updatedWorkflows);

  try {
    // Save to backend usersettings
    await SettingsService.saveUserSettings({ workflows: updatedWorkflows });

    // Sync userStore
    userStore.update((u) => ({
      ...u,
      settings: { ...u.settings, workflows: updatedWorkflows }
    }));

    return newWorkflow;
  } catch (error) {
    // Rollback on error
    workflowsStore.set(workflows);
    throw error;
  }
};

// Update an existing workflow
export const updateWorkflow = async (workflowName, workflowData) => {
  const workflows = get(workflowsStore);
  const previousState = [...workflows];

  const updatedWorkflows = workflows.map((workflow) => {
    if (workflow.name === workflowName) {
      return {
        name: workflowData.name || workflowName,
        steps: workflowData.steps
      };
    }
    return workflow;
  });

  // Optimistic update
  workflowsStore.set(updatedWorkflows);

  try {
    await SettingsService.saveUserSettings({ workflows: updatedWorkflows });

    userStore.update((u) => ({
      ...u,
      settings: { ...u.settings, workflows: updatedWorkflows }
    }));

    return updatedWorkflows.find((w) => w.name === (workflowData.name || workflowName));
  } catch (error) {
    workflowsStore.set(previousState);
    throw error;
  }
};

// Clone a workflow
export const cloneWorkflow = async (workflowName) => {
  const workflows = get(workflowsStore);
  const workflowToClone = workflows.find((workflow) => workflow.name === workflowName);

  if (!workflowToClone) {
    throw new Error('Workflow not found');
  }

  const clonedWorkflow = {
    name: `${workflowToClone.name}_clone_${generateUUID()}`,
    steps: workflowToClone.steps
  };

  const updatedWorkflows = [...workflows, clonedWorkflow];

  // Optimistic update
  workflowsStore.set(updatedWorkflows);

  try {
    await SettingsService.saveUserSettings({ workflows: updatedWorkflows });

    userStore.update((u) => ({
      ...u,
      settings: { ...u.settings, workflows: updatedWorkflows }
    }));

    return clonedWorkflow;
  } catch (error) {
    workflowsStore.set(workflows);
    throw error;
  }
};

// Delete a workflow
export const deleteWorkflow = async (workflowName) => {
  const workflows = get(workflowsStore);
  const previousState = [...workflows];

  const filteredWorkflows = workflows.filter((workflow) => workflow.name !== workflowName);

  // Optimistic update
  workflowsStore.set(filteredWorkflows);

  try {
    await SettingsService.saveUserSettings({ workflows: filteredWorkflows });

    userStore.update((u) => ({
      ...u,
      settings: { ...u.settings, workflows: filteredWorkflows }
    }));

    return filteredWorkflows;
  } catch (error) {
    workflowsStore.set(previousState);
    throw error;
  }
};

// Export a workflow to JSON
export const exportWorkflow = (workflowName) => {
  const workflows = get(workflowsStore);
  const workflow = workflows.find((w) => w.name === workflowName);

  if (!workflow) {
    throw new Error('Workflow not found');
  }

  return workflow;
};

// Import a workflow from JSON
export const importWorkflow = async (workflowData) => {
  // Validate workflow data before storing
  const validation = validateWorkflowForImport(workflowData);
  if (!validation.valid) {
    throw new Error(`Invalid workflow: ${validation.errors[0]}`);
  }

  const workflows = get(workflowsStore);

  // Check if workflow with same name exists
  const nameExists = workflows.some((w) => w.name.toLowerCase() === workflowData.name.toLowerCase());

  let workflowName = workflowData.name;
  if (nameExists) {
    // Append a suffix to make it unique
    workflowName = `${workflowData.name}_imported_${generateUUID()}`;
  }

  const newWorkflow = {
    name: workflowName,
    steps: workflowData.steps
  };

  const updatedWorkflows = [...workflows, newWorkflow];

  // Optimistic update
  workflowsStore.set(updatedWorkflows);

  try {
    await SettingsService.saveUserSettings({ workflows: updatedWorkflows });

    userStore.update((u) => ({
      ...u,
      settings: { ...u.settings, workflows: updatedWorkflows }
    }));

    return newWorkflow;
  } catch (error) {
    workflowsStore.set(workflows);
    throw error;
  }
};
